/**
 * Strumenti (ADR-0033 Phase D) — LISTA. Uno strumento è una funzione PURA che ritorna un
 * valore, eseguita nel sandbox isolato (senza rete, senza credenziali). L'editor vive
 * nella pagina di dettaglio a schermo intero (`/ai/tools/:id`, `ToolDetailPage`). Gli
 * strumenti si collegano agli agenti personalizzati (pagina Agenti). Area `ai`.
 */
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2, Wrench } from "lucide-react";

import { usePermissions } from "@/lib/permissions";
import { toolsApi } from "@/lib/api-ai";

const btnCls =
  "inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-sm hover:bg-muted disabled:opacity-50";

const KEY = ["ai", "tools"] as const;

export function ToolsPage() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canWrite = can("tools", "write");
  const canDelete = can("tools", "delete");
  const qc = useQueryClient();
  const q = useQuery({ queryKey: KEY, queryFn: () => toolsApi.list() });

  const remove = useMutation({
    mutationFn: (id: string) => toolsApi.remove(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });

  const tools = q.data?.tools ?? [];

  return (
    <div className="mx-auto max-w-4xl p-6">
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-lg font-semibold">
            <Wrench className="h-4 w-4 text-muted-foreground" /> Strumenti
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Funzioni riutilizzabili che scrivi in codice (Python o TypeScript) ed esegui nel
            sandbox isolato (senza rete, senza credenziali). Uno strumento riceve solo i
            propri argomenti e file; non accede mai ai dati di una sorgente. Collega uno
            strumento a un agente personalizzato dalla pagina Agenti.
          </p>
        </div>
        {canWrite && (
          <button type="button" className={btnCls} onClick={() => navigate("/ai/tools/new")}>
            <Plus className="h-4 w-4" /> Nuovo
          </button>
        )}
      </div>

      {q.isLoading && <p className="text-sm text-muted-foreground">Caricamento…</p>}
      {tools.length === 0 && !q.isLoading ? (
        <p className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          Nessuno strumento. Creane uno per esempio “riassumi un PDF” o “valida un IBAN”.
        </p>
      ) : (
        tools.length > 0 && (
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="py-2">Strumento</th>
                <th className="py-2">Linguaggio</th>
                <th className="py-2">Stato</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {tools.map((t) => (
                <tr
                  key={t.id}
                  className="cursor-pointer border-b border-border/60 align-top hover:bg-muted/40"
                  onClick={() => navigate(`/ai/tools/${t.id}`)}
                >
                  <td className="py-2 font-medium">
                    {t.name}
                    <span className="ml-2 rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                      {t.key}
                    </span>
                    {t.description && (
                      <p className="mt-0.5 text-xs font-normal text-muted-foreground">{t.description}</p>
                    )}
                  </td>
                  <td className="py-2 text-xs text-muted-foreground">
                    {t.language === "javascript" ? "TypeScript" : "Python"}
                  </td>
                  <td className="py-2">
                    <span className={t.enabled ? "text-success text-xs" : "text-muted-foreground text-xs"}>
                      {t.enabled ? "attivo" : "disattivato"}
                    </span>
                  </td>
                  <td className="py-2">
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="rounded p-1 hover:bg-muted"
                        title={canWrite ? "Modifica" : "Apri"}
                        onClick={() => navigate(`/ai/tools/${t.id}`)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      {canDelete && (
                        <button
                          type="button"
                          className="rounded p-1 text-danger hover:bg-muted"
                          title="Elimina"
                          disabled={remove.isPending}
                          onClick={() => {
                            if (window.confirm(`Eliminare lo strumento “${t.name}”?`)) remove.mutate(t.id);
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}
    </div>
  );
}
